import React, { useMemo } from "react";
import "./PrivacyPolicy.css";
import { useLocalContext } from "../../context/LocalContext";

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients worldwide",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Privacy & data use</p>
          <h1>Privacy Policy</h1>
          <p>
            At {brand.name}, we respect your time, attention, and privacy. This
            page explains what we collect, why we collect it, and the choices
            you have when you interact with our website, campaigns, or services.
          </p>
          <p className="pp-meta">
            <span>Controller: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
          </p>
        </header>

        <div className="privacy-policy-content">
          <section>
            <h2>1. Who we are</h2>
            <p>
              This Privacy Policy describes how {brand.name} (“we”, “us”, or
              “our”) handles personal information collected through our
              website, forms, campaigns, and related services. By using our
              website or working with us, you agree to the practices described
              here.
            </p>
          </section>

          <section>
            <h2>2. Information we collect</h2>
            <p>We collect information in three main ways:</p>
            <ul>
              <li>
                <strong>Information you share with us</strong> – such as your
                name, email address, phone number, job title, company name,
                project details, or any other information you include when you
                fill in a form, book a call, or contact us directly.
              </li>
              <li>
                <strong>Automatic data</strong> – limited technical data like
                IP address, device type, browser, pages viewed, time on page,
                and referring URLs, typically through analytics tools.
              </li>
              <li>
                <strong>Cookies & similar technologies</strong> – small files
                used to remember your preferences, measure performance, and
                improve how our site works.
              </li>
            </ul>
          </section>

          <section>
            <h2>3. How we use your information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Respond to inquiries, proposals, and support requests.</li>
              <li>
                Provide, operate, and improve our website, services, and
                marketing.
              </li>
              <li>
                Personalize content and communication that may be relevant to
                your interests.
              </li>
              <li>
                Maintain security, prevent misuse, and comply with legal and
                contractual obligations.
              </li>
            </ul>
          </section>

          <section>
            <h2>4. Legal bases for processing</h2>
            <p>
              Where data-protection laws apply (for example, in the EU/EEA or
              UK), we rely on one or more of the following legal bases:
            </p>
            <ul>
              <li>
                <strong>Consent</strong> – when you actively opt in (for
                example, submitting a contact form or accepting certain
                cookies).
              </li>
              <li>
                <strong>Legitimate interests</strong> – such as improving our
                services, maintaining security, and communicating with you about
                relevant services, where these interests are not overridden by
                your rights.
              </li>
              <li>
                <strong>Contract</strong> – when processing is necessary to
                deliver a project or service you’ve asked us to provide.
              </li>
              <li>
                <strong>Legal obligations</strong> – where we must retain or
                disclose data for compliance, tax, or regulatory reasons.
              </li>
            </ul>
          </section>

          <section>
            <h2>5. Cookies & analytics</h2>
            <p>
              Our website may use cookies, tags, pixels, and analytics tools
              (such as Google Analytics or similar services) to understand
              how visitors use the site and to improve performance.
            </p>
            <p>
              You can manage or disable cookies in your browser settings. Some
              features may not work as expected if certain cookies are disabled.
            </p>
          </section>

          <section>
            <h2>6. Marketing & communication</h2>
            <p>
              We may use your contact details to send project-related updates,
              invitations to calls, or information about services that may be
              relevant to you or your company.
            </p>
            <p>
              You can opt out of non-essential marketing at any time by using
              the unsubscribe option (where available) or by contacting us
              directly.
            </p>
          </section>

          <section>
            <h2>7. When we share your data</h2>
            <p>
              We do <strong>not</strong> sell your personal data. We may share
              information with carefully selected third parties who help us run
              our operations, such as:
            </p>
            <ul>
              <li>Hosting and infrastructure providers.</li>
              <li>Analytics and performance tools.</li>
              <li>Payment and invoicing platforms (for paid services).</li>
              <li>
                Professional advisors (legal, accounting) where reasonably
                necessary.
              </li>
            </ul>
            <p>
              These partners are only given access to the information they
              need, and they are required to handle it securely and in line with
              applicable privacy rules.
            </p>
          </section>

          <section>
            <h2>8. International transfers</h2>
            <p>
              Our tools and service providers may be located in different
              countries. This means your data can be processed in locations with
              different data-protection laws than your home country.
            </p>
            <p>
              Where required, we take steps to ensure an adequate level of
              protection (for example, by using standard contractual clauses
              or equivalent safeguards).
            </p>
          </section>

          <section>
            <h2>9. Data retention</h2>
            <p>
              We keep personal data only for as long as it is needed for the
              purposes described in this policy, or as long as required by law
              (for example, for tax or accounting rules).
            </p>
            <p>
              When data is no longer needed, we aim to delete it securely or
              anonymize it so it can no longer be linked back to you.
            </p>
          </section>

          <section>
            <h2>10. Your choices & rights</h2>
            <p>
              Depending on your location and local laws, you may have the right
              to:
            </p>
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Request corrections to inaccurate or incomplete data.</li>
              <li>Request deletion of your personal information.</li>
              <li>
                Object to or restrict certain types of processing, including
                direct marketing.
              </li>
              <li>Request a copy of your data in a portable format.</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the details in
              the <strong>Contact us</strong> section below. We may need to
              verify your identity before responding.
            </p>
          </section>

          <section>
            <h2>11. Security</h2>
            <p>
              We use reasonable technical and organizational measures to protect
              personal data against unauthorized access, loss, misuse, or
              alteration. However, no method of transmission or storage is
              completely risk-free, so we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>12. Children’s privacy</h2>
            <p>
              Our services are intended for business and professional use. We do
              not knowingly collect personal information from children under 13
              (or under 16 in certain regions). If you believe a child has
              provided us with personal data, please contact us so we can remove
              it.
            </p>
          </section>

          <section>
            <h2>13. Third-party websites</h2>
            <p>
              Our website may include links to third-party sites, tools, or
              platforms. {brand.name} is not responsible for the privacy
              practices or content of those websites. We recommend reviewing
              their privacy policies before sharing any information.
            </p>
          </section>

          <section>
            <h2>14. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in law, technology, or our services. When we make
              material changes, we will update the date at the top of this page
              and, where appropriate, provide additional notice.
            </p>
          </section>

          <section>
            <h2>15. Contact us</h2>
            <p>
              If you have questions, concerns, or requests regarding this
              Privacy Policy or how we handle personal data, you can contact us
              at:
            </p>
            <ul className="pp-contact-list">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {brand.email}
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                  {brand.phone}
                </a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
